import React from 'react';
import type { WebsiteComponent } from '@/interfaces/component.interface';
export type ElementType =
  | 'heading'
  | 'paragraph'
  | 'button'
  | 'image'
  | 'container'
  | 'video-container'
  | 'logo'
  | 'divider'
  | 'gallery'
  | 'icon'
  | 'dynamic-icon'
  | 'custom-icon'
  | 'text'
  | 'link'
  | 'list'
  | 'listItem'
  | 'video'
  | 'map'
  | 'input'
  | 'select'
  | 'checkbox'
  | 'textarea'
  | 'carousel'
  | 'accordion'
  | 'vidalytics-video'
  | 'timer'
  | 'form'
  | 'hamburger-menu'
  | 'html'
  | 'table'
  | 'file-upload'
  | 'dropdown-menu'
  | 'progress-bar'
  | 'tab-container'
  | 'form-group'
  | 'root-container'
  | 'timeline'
  | 'close-popup-button';

export type ViewMode = 'desktop' | 'tablet' | 'mobile';

export type TextAlignment = 'left' | 'center' | 'right';

export type BuilderStage = 'registration' | 'preview' | 'editor' | 'publish';

export type Industry =
  | 'template1'
  | 'template2'
  | 'template3'
  | 'template4'
  | 'template4-upsell'
  | 'template4-downsell'
  | 'restaurant'
  | 'templateUpsell3'
  | 'templateDownsell3'
  | 'template2-upsell'
  | 'template2-downsell'
  | 'template2-thankyou'
  | 'template2-contact'
  | 'template2-privacy-policy'
  | 'template2-terms'
  | 'template2-returns'
  | 'template2-disclaimer'
  | 'template3thankyou'
  | 'template3contactUs'
  | 'template3PrivacyPolicy'
  | 'template3terms'
  | 'template3refund'
  | 'template3disclaimer'
  | 'template1-upsell'
  | 'template1-downsell'
  | 'template1contactUs'
  | 'template1Thankyou'
  | 'template1Privacy'
  | 'template1Refund'
  | 'template1terms'
  | 'template1Disclaimer'
  | 'template1VSL'
  | 'template2VSL'
  | 'blankTemplate';

export interface UserConfig {
  businessName: string;
  industry: Industry;
  logo?: string | ResponsiveValue<string>;
  globalConfig: GlobalConfig;
}

export interface TypographyToken {
  name: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  fontStyle: string;
}

export interface GlobalConfig {
  typography: TypographyToken[];
  colors: Record<string, string>;
  breakpoints: {
    mobileMax: number; // e.g., 640
    tabletMin: number; // e.g., 641
    tabletMax: number; // e.g., 1024
    desktopMin: number; // e.g., 1025
  };
}

export type ResponsiveValue<T> = {
  desktop: T;
  tablet?: T;
  mobile?: T;
};

export interface BuilderElement {
  id: string;
  type: ElementType;
  listTag?: 'ul' | 'ol';
  vslShowHide?: boolean;
  content?: string;
  href?: string;
  target?: string;
  src?: string;
  alt?: string;
  poster?: string;
  playbackSpeed?: 'slow' | 'normal' | 'fast'; // Video playback speed
  parentTabId?: string; // New: To identify which tab this element belongs to
  // Video container specific properties
  videoSrc?: string; // Video source URL for video container
  videoPoster?: string; // Video poster image for video container
  videoSettings?: {
    autoplay?: boolean;
    muted?: boolean;
    loop?: boolean; // Loop the video (works for both regular and YouTube videos)
    controls?: boolean;
    playbackSpeed?: 'slow' | 'normal' | 'fast';
    // YouTube specific settings (auto-detected when videoSrc is YouTube URL)
    showYouTubeControls?: boolean;
    startTime?: number; // Start time in seconds
    endTime?: number; // End time in seconds
    enableKeyboard?: boolean;
    enableFullscreen?: boolean;
    disableRelatedVideos?: boolean; // Hide related videos at the end
    hideYouTubeInfo?: boolean; // Hide video title, uploader info, and share buttons
    hideYouTubeAnnotations?: boolean; // Hide annotations and info cards
  };
  // Map specific properties
  mapSettings?: {
    latitude?: number;
    longitude?: number;
    zoom?: number;
    mapType?: 'roadmap' | 'satellite' | 'hybrid' | 'terrain';
    showControls?: boolean;
    showMarkers?: boolean;
    markerTitle?: string;
    markerDescription?: string;
    apiKey?: string; // Google Maps API key
    // Enhanced map features
    showTraffic?: boolean;
    showTransit?: boolean;
    showBicycling?: boolean;
    showStreetView?: boolean;
    showKmlLayers?: boolean;
    kmlUrl?: string;
    customMapStyle?: string; // JSON string for custom map styling
    mapLanguage?: string; // Language code for map labels
    mapRegion?: string; // Region code for map bias
    gestureHandling?: 'auto' | 'cooperative' | 'greedy' | 'none';
    disableDefaultUI?: boolean;
    zoomControl?: boolean;
    mapTypeControl?: boolean;
    scaleControl?: boolean;
    streetViewControl?: boolean;
    rotateControl?: boolean;
    fullscreenControl?: boolean;
  };
  htmlContent?: string; // Raw HTML code for html elements
  useEnhancedEditor?: boolean; // Use enhanced HTML editor with separate CSS/JS tabs
  typography?: string;
  name?: string;
  tailwindClassName?: string;
  placeholder?: string;
  // Form input properties
  label?: string;
  inputType?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number;
  enctype?: string;
  inputStyle?: 'classic' | 'outline'; // Input styling mode: classic (label outside) or outline (label inside)
  dynamicContentId?: string;
  queryParamContentKey?: string;
  queryParamHrefKey?: string;
  queryParamSrcKey?: string;
  triggerPopup?: string;
  // New interaction properties
  interactionType?:
    | 'none'
    | 'popup'
    | 'link'
    | 'scroll-to-section'
    | 'navigate-to-page'
    | 'close-popup';
  linkUrl?: string;
  linkTarget?: '_self' | '_blank' | '_parent' | '_top';
  // Section scrolling properties
  targetSectionId?: string;
  scrollOffset?: number; // Optional offset for fixed headers
  clickable?: boolean; // Whether the element is clickable
  // Page navigation properties
  targetPageId?: string; // ID of the target page to navigate to
  // Section identification (for containers that can be linked to)
  isLinkableSection?: boolean;
  sectionName?: string; // Human-readable name for the section
  // Custom active link styling for scroll-to-section links
  customActiveLinkStyle?: string;
  // Animate.css animation properties (legacy - kept for backward compatibility)
  animationName?: string; // e.g., "bounce", "fadeIn", "slideInLeft"
  animationDuration?: string; // e.g., "1s", "2s", "500ms"
  animationDelay?: string; // e.g., "0s", "1s", "500ms"
  animationIterationCount?: string; // e.g., "1", "infinite", "3"
  animationEnabled?: boolean; // Whether animation is enabled
  // New multi-animation support
  animations?: Array<{
    id: string;
    enabled: boolean;
    name: string;
    duration: string;
    delay: string;
    iterationCount: string;
  }>;
  // Button icon properties
  icon?: {
    src?: string;
    lucideIconName?: string; // Lucide icon name (e.g., "Heart", "ArrowRight")
    position?: 'left' | 'right';
    size?: number;
    spacing?: number;
    alt?: string;
    color?: string; // Icon color (primarily for Lucide icons)
  };
  // Icon element size property
  iconSize?: number;
  // Root container properties for global color management
  rootContainer?: {
    isRootContainer?: boolean;
    globalColors?: {
      primary?: string;
      secondary?: string;
      tertiary?: string;
      quaternary?: string;
      background?: string;
      foreground?: string;
      link?: string;
      activeLink?: string;
    };
  };
  styles: {
    color?: string;
    background?: string;
    backgroundColor?: string;
    backgroundImage?: string; // Added for background images
    fontSize?: string;
    fontWeight?: string;
    fontStyle?: string; // Added fontStyle property
    textAlign?: string;
    padding?: string;
    paddingTop?: string;
    paddingBottom?: string;
    paddingLeft?: string;
    paddingRight?: string;
    margin?: string;
    overflow?: string;
    borderRadius?: string;
    width?: string;
    height?: string;
    minHeight?: string;
    boxShadow?: string;
    textShadow?: string;
    border?: string;
    borderBottom?: string;
    borderTop?: string;
    borderLeft?: string;
    borderRight?: string;
    fontFamily?: string;
    display?: string; // Added display property
    flexDirection?: string;
    flexWrap?: string;
    listStyle?: string;
    listStyleType?: string;
    listStylePosition?: string;
    counterReset?: string;
    gridTemplateColumns?: string; // Added for gallery layout
    gap?: string; // Added for spacing in layouts
    position?: string; // Added for positioning elements
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
    transform?: string;
    transition?: string;
    objectFit?: string; // Added for image sizing
    maxWidth?: string; // Added for limiting widths
    maxHeight?: string; // Added for limiting Heights
    // CSS Filter properties for image effects
    hueRotate?: string; // Hue rotation in degrees
    saturate?: string; // Saturation multiplier
    brightness?: string; // Brightness multiplier
    contrast?: string; // Contrast multiplier
    blur?: string; // Blur in pixels
    grayscale?: string; // Grayscale (0-1)
    sepia?: string; // Sepia (0-1)
    invert?: string; // Invert (0-1)
    opacity?: string; // Opacity (0-1)
    marginLeft?: string; // Added for specific margin directions
    marginRight?: string;
    marginTop?: string;
    marginBottom?: string;
    justifyContent?: string; // Added for flex layouts
    alignItems?: string; // Added for flex layouts
    zIndex?: string;
    backgroundSize?: string; // Added for background image sizing
    backgroundPosition?: string; // Added for background image positioning
    backgroundRepeat?: string; // Added for background image repeat
    backgroundAttachment?: string; // Added for background image attachment
    animation?: string; // Added for animations like marquee
    minWidth?: string; // Added for minimum width control
    whiteSpace?: string; // Added for text wrapping control
    cursor?: string; // Added for cursor styling
    lineHeight?: string;
    textDecoration?: string;
    letterSpacing?: string;
    alignSelf?: string;
    progress?: number; // Added for progress bar percentage
    showPercentage?: boolean; // Added for progress bar percentage visibility
    labelPosition?:
      | 'left'
      | 'right'
      | 'top'
      | 'bottom'
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'; // Added for progress bar label position
    progressCorners?: 'none' | 'rounded' | 'pill'; // Added for progress bar corner styling
    // Sticky positioning backgrounds
    stickyTopBackground?: string; // Background when element is in normal position
    stickyScrollBackground?: string; // Background when element is stuck during scroll
  };
  responsiveStyles?: {
    tablet?: Partial<BuilderElement['styles']>;
    mobile?: Partial<BuilderElement['styles']>;
  };
  // Responsive image sources (per-breakpoint). Default/fallback remains `src`.
  srcResponsive?: {
    desktop?: string;
    tablet?: string;
    mobile?: string;
  };
  // Hover styles support
  hoverStyles?: {
    color?: string;
    backgroundColor?: string;
    backgroundImage?: string;
    fontSize?: string;
    fontWeight?: string;
    fontStyle?: string;
    textAlign?: string;
    padding?: string;
    margin?: string;
    overflow?: string;
    borderRadius?: string;
    borderTopLeftRadius?: string;
    borderTopRightRadius?: string;
    borderBottomLeftRadius?: string;
    borderBottomRightRadius?: string;
    width?: string;
    height?: string;
    minHeight?: string;
    boxShadow?: string;
    textShadow?: string;
    border?: string;
    fontFamily?: string;
    display?: string;
    flexDirection?: string;
    flexWrap?: string;
    listStyle?: string;
    listStyleType?: string;
    listStylePosition?: string;
    counterReset?: string;
    gridTemplateColumns?: string;
    gap?: string;
    position?: string;
    top?: string;
    left?: string;
    bottom?: string;
    right?: string;
    transform?: string;
    transition?: string;
    objectFit?: string;
    maxWidth?: string;
    maxHeight?: string;
    marginLeft?: string;
    marginRight?: string;
    justifyContent?: string;
    alignItems?: string;
    zIndex?: string;
    backgroundSize?: string;
    backgroundPosition?: string;
    backgroundRepeat?: string;
    backgroundAttachment?: string;
    animation?: string;
    minWidth?: string;
    whiteSpace?: string;
    cursor?: string;
    lineHeight?: string;
    // Text decoration properties for links
    textDecoration?: string;
    textDecorationColor?: string;
    textDecorationStyle?: string;
    textDecorationThickness?: string;
    textUnderlineOffset?: string;
    opacity?: string;
  };
  responsiveHoverStyles?: {
    tablet?: Partial<BuilderElement['hoverStyles']>;
    mobile?: Partial<BuilderElement['hoverStyles']>;
  };
  children?: BuilderElement[];
  options?: Array<{ value: string; label: string }>; // For select elements
  autoPlay?: ResponsiveValue<boolean>;
  loop?: ResponsiveValue<boolean>;
  pagination?: ResponsiveValue<boolean>;
  navigation?: ResponsiveValue<boolean>;
  slidesPerView?: ResponsiveValue<number>;
  spaceBetween?: ResponsiveValue<number>;
  autoplayDelay?: ResponsiveValue<number>;
  fullscreenOnClick?: boolean; // Enable fullscreen/lightbox view on slide click
  // Enhanced pagination configuration
  paginationStyle?: 'dots' | 'lines' | 'numbers' | 'custom';
  paginationPosition?:
    | 'bottom-center'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-center'
    | 'middle-left'
    | 'middle-right';
  paginationShape?:
    | 'circle'
    | 'rectangle'
    | 'bordered-circle'
    | 'bordered-rectangle'
    | 'none';
  paginationDotSize?: number; // in pixels
  paginationSpacing?: number; // in pixels
  paginationActiveColor?: string;
  paginationInactiveColor?: string;
  paginationBorderWidth?: number; // in pixels
  paginationBorderColor?: string;
  paginationDirection?: 'horizontal' | 'vertical'; // Direction of pagination dots
  allowMultiple?: boolean;
  defaultOpen?: boolean;
  accordionType?: 'single' | 'multiple';
  // Accordion-specific properties
  accordionRole?: 'question' | 'answer';
  accordionClickHandler?: (e: React.MouseEvent) => void; // Custom click handler for accordion questions
  // Accordion-level icon configuration (for accordion container only)
  iconSrcCollapsed?: string; // Icon source when accordion items are collapsed
  iconSrcExpanded?: string; // Icon source when accordion items are expanded
  iconPosition?: 'before' | 'after'; // Icon position relative to question text
  // Accordion icon file metadata (for uploaded icons)
  iconCollapsedFileType?: string; // MIME type of uploaded collapsed icon
  iconCollapsedOriginalFileName?: string; // Original filename of uploaded collapsed icon
  iconExpandedFileType?: string; // MIME type of uploaded expanded icon
  iconExpandedOriginalFileName?: string; // Original filename of uploaded expanded icon
  // Accordion icon styling (for accordion container only)
  iconStyles?: {
    width?: string;
    height?: string;
    marginLeft?: string;
    marginRight?: string;
  };
  // Accordion item border radius (controls the border radius of individual accordion items)
  itemBorderRadius?: string;
  // Accordion item border (controls the border of individual accordion items)
  itemBorder?: string;
  playerEvents?: {
    play?: () => void;
    pause?: () => void;
    ended?: () => void;
  };
  countdownTimerSeconds?: number;
  videoVariant?: number; // Added for vidalytics-video variant selection
  // --- Added for vidalytics-video custom URLs ---
  landscapeEmbedId?: string; // For vidalytics-video (landscape)
  verticalEmbedId?: string; // For vidalytics-video (vertical)
  landscapeUrl?: string;
  verticalUrl?: string;
  // File type information for uploaded assets
  fileType?: string; // MIME type of uploaded file (e.g., "image/jpeg", "video/mp4")
  originalFileName?: string; // Original filename from upload
  // Element visibility control
  visible?: boolean; // Controls whether element is visible on the page
  // Responsive visibility control (per breakpoint)
  responsiveVisible?: {
    desktop?: boolean;
    tablet?: boolean;
    mobile?: boolean;
  };
  isSubmitButton?: boolean;
  // Table-specific properties
  tableData?: {
    headers: string[];
    rows: string[][];
  };
  tableConfig?: {
    hasHeader?: boolean;
    stripedRows?: boolean;
    bordered?: boolean;
    responsive?: boolean;
  };
  dropdownConfig?: {
    label: string;
    trigger: 'hover' | 'click';
    alignment?: 'left' | 'right' | 'center';
    direction?: 'bottom' | 'right' | 'left';
    width?: string;
    widthMode?: 'auto' | 'full' | 'custom';
    dropdownMobileBehavior?: 'stack' | 'toggle' | 'none';
    appearance?: 'text' | 'button';
    fullWidthAlignment?: 'left' | 'center' | 'right';
    icon?: {
      mode?: 'builtin' | 'lucide' | 'custom';
      type?: 'chevron-left' | 'chevron-right' | 'chevron-down';
      position?: 'left' | 'right';
      size?: number;
      lucideIconName?: string;
      customSrc?: string;
      customSvgMarkup?: string;
      customAlt?: string;
      /**
       * @deprecated Use `size` instead.
       */
      customSize?: number;
    };
  };
  // Tab container specific properties
  tabConfig?: {
    tabs: Array<{
      id: string;
      label: string;
      // content: BuilderElement[]; // Removed: Tab content will now be handled as children with parentTabId
    }>;
    activeTabId?: string;
    triggerType?: 'button' | 'link';
    alignment?: 'left' | 'center' | 'right';
    spacing?: string;
    activeTriggerStyle?: {
      backgroundColor?: string;
      color?: string;
      border?: string;
      borderRadius?: string;
      fontWeight?: string;
      textDecoration?: string;
      borderColor?: string;
      borderWidth?: string;
      borderStyle?: string;
      borderPosition?: 'all' | 'bottom' | 'top';
      padding?: string;
      fontSize?: string;
    };
    inactiveTriggerStyle?: {
      backgroundColor?: string;
      color?: string;
      border?: string;
      borderRadius?: string;
      fontWeight?: string;
      textDecoration?: string;
      borderColor?: string;
      borderWidth?: string;
      borderStyle?: string;
      borderPosition?: 'all' | 'bottom' | 'top';
      padding?: string;
      fontSize?: string;
    };
    triggerHoverStyle?: {
      backgroundColor?: string;
      color?: string;
      border?: string;
      borderRadius?: string;
      fontWeight?: string;
      textDecoration?: string;
      borderColor?: string;
      borderWidth?: string;
      borderStyle?: string;
      borderPosition?: 'all' | 'bottom' | 'top';
      padding?: string;
      fontSize?: string;
    };
    contentAreaStyle?: {
      backgroundColor?: string;
      border?: string;
      borderRadius?: string;
      padding?: string;
      margin?: string;
    };
  };
  carouselConfig?: {
    carouselMode?: 'classic' | 'background';
    dropTarget?: 'main' | 'slides';
    // Classic mode hover overlay container
    hoverEnabled?: boolean;
    // When true in editor, drops go to hover container instead of slides
    dropIntoHover?: boolean;
  };
  // Timeline-specific properties
  timelineEntries?: Array<{
    id: string;
    year: string;
    title: string;
    subtitle: string;
    description: string;
  }>;
  timelineConfig?: {
    lineColor?: string;
    markerSize?: string;
    markerColor?: string;
    lineWidth?: string;
    orientation?: 'vertical' | 'horizontal';
    alignment?: 'left' | 'center' | 'right';
  };
  // Hamburger menu specific properties
  hamburgerAnimationState?: 'open' | 'closed'; // State for hamburger menu animation
  hamburgerAnimationDuration?: string; // Animation duration (e.g., "0.3s", "500ms")
  popupAnimationOnShow?: string; // Animation name for popup when showing (e.g., "fadeIn", "slideInDown")
  popupAnimationOnClose?: string; // Animation name for popup when closing (e.g., "fadeOut", "slideOutUp")
  // Sync metadata flags (not persisted to API, used for tracking sync state)
  _needsSync?: boolean; // Element needs to be synced to API
  _isNew?: boolean; // Element is newly created (needs create, not update)
}

export interface Template {
  id: string;
  name: string;
  industry: Industry;
  elements: BuilderElement[];
  thumbnail?: string;
  rootContainer?: {
    styles?: {
      backgroundColor?: string;
      color?: string;
      padding?: string;
      margin?: string;
      width?: string;
      minHeight?: string;
      display?: string;
      position?: string;
      // Scrollbar customization
      scrollbarThumbColor?: string;
      scrollbarTrackColor?: string;
      scrollbarWidth?: string;
      scrollbarHeight?: string;
      scrollbarBorderRadius?: string;
      [key: string]: string | number | undefined;
    };
    responsiveStyles?: {
      desktop?: {
        [key: string]: string | number | undefined;
      };
      tablet?: {
        [key: string]: string | number | undefined;
      };
      mobile?: {
        [key: string]: string | number | undefined;
      };
    };
    hoverStyles?: {
      [key: string]: string | number | undefined;
    };
    responsiveHoverStyles?: {
      desktop?: {
        [key: string]: string | number | undefined;
      };
      tablet?: {
        [key: string]: string | number | undefined;
      };
      mobile?: {
        [key: string]: string | number | undefined;
      };
    };
  };
}

export interface BuilderHistoryEntry {
  template: Template;
  popupTemplates: Template[];
}

export interface BuilderHistory {
  past: BuilderHistoryEntry[];
  future: BuilderHistoryEntry[];
}

export interface BuilderState {
  template: Template;
  selectedElementId: string | null;
  viewMode: ViewMode;
  isDragging: boolean;
  stage: BuilderStage;
  userConfig: UserConfig | null;
  history: BuilderHistory;
  zoom: number;
  extraPages: TemplateExtraPage[];
  popupTemplates: Template[];
  selectedPage: string;
  selectedInterfaceId: string;
  selectedPopupID?: string;
  dynamicValues?: TemplateExtraPageDynamicValues[];
  dynamicElementValues?: { [queryKey: string]: string };
  selectedDynamicValue?: { queryKey: string; value: string } | null;
  siteSettings: PageSiteSettings;
  generalSettings?: SiteSettings;
  carouselActiveIndex?: { [elementId: string]: number };
  components?: WebsiteComponent[];
  isCreatingInterfaces: boolean;
  // Bulk import tracking
  bulkImportIds: string[];
  interfaceBulkImportIds: string[];
  interfaceBulkImportMetadata: Map<string, {
    moduleId: string;
    industry: string;
    pageTypes: string[];
  }>;
  isSavingElements: boolean;
}

export type BuilderAction =
  | { type: 'SET_TEMPLATE'; payload: Template }
  | { type: 'ADD_EXTRA_PAGE'; payload: TemplateExtraPage }
  | { type: 'SET_INTERFACES'; payload: Array<{ id: string; title: string; page_type: string }> }
  | {
      type: 'UPDATE_TEMPLATE_ROOT_CONTAINER';
      payload: {
        styles?: { [key: string]: string | number | undefined };
        responsiveStyles?: {
          desktop?: { [key: string]: string | number | undefined };
          tablet?: { [key: string]: string | number | undefined };
          mobile?: { [key: string]: string | number | undefined };
        };
        hoverStyles?: { [key: string]: string | number | undefined };
        responsiveHoverStyles?: {
          desktop?: { [key: string]: string | number | undefined };
          tablet?: { [key: string]: string | number | undefined };
          mobile?: { [key: string]: string | number | undefined };
        };
      };
    }
  | { type: 'SELECT_ELEMENT'; payload: string | null }
  | {
      type: 'UPDATE_ELEMENT';
      payload: { id: string; element: BuilderElement; parentTabId?: string };
    }
  | {
      type: 'MARK_ELEMENT_AS_SYNCED';
      payload: { elementId: string };
    }
  | {
      type: 'ADD_ELEMENT';
      payload: {
        type: ElementType;
        containerId: string;
        iconContent?: string;
        parentTabId?: string;
      };
    }
  | { type: 'ADD_ROOT_ELEMENT'; payload: BuilderElement }
  | {
      type: 'UPDATE_TAB_CONFIG';
      payload: { id: string; tabConfig: BuilderElement['tabConfig'] };
    }
  | {
      type: 'DUPLICATE_ELEMENT';
      payload: {
        elementId: string;
        targetContainerId: string;
        targetIndex: number;
      };
    }
  | { type: 'SET_VIEW_MODE'; payload: ViewMode }
  | { type: 'SET_DRAGGING'; payload: boolean }
  | { type: 'SAVE_TEMPLATE' }
  | { type: 'SET_STAGE'; payload: BuilderStage }
  | { type: 'SET_USER_CONFIG'; payload: UserConfig; landingPageID?: string }
  | { type: 'REMOVE_ELEMENT'; payload: string }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  | { type: 'RESET_HISTORY' }
  | { type: 'GENERATE_TEMPLATE'; payload: string }
  | { type: 'SET_ZOOM'; payload: number }
  | { type: 'RESET_ZOOM' }
  | { type: 'CHANGE_TEMPLATE_PAGE'; payload: string }
  | { type: 'SET_SELECTED_PAGE'; payload: string }
  | { type: 'SET_SELECTED_INTERFACE_ID'; payload: string }
  | { type: 'SELECT_POPUP_PAGE'; payload: string }
  | { type: 'ADD_POPUP_TEMPLATE'; payload: Template }
  | {
      type: 'UPDATE_POPUP_TEMPLATE';
      payload: { id: string; template: Template };
    }
  | { type: 'DELETE_POPUP_TEMPLATE'; payload: string }
  | { type: 'SET_UNSAVED_CHANGES'; payload: boolean }
  | {
      type: 'SET_GENERAL_SETTINGS';
      payload: SiteSettings;
      landingPageID?: string;
    }
  | { type: 'SET_SITE_SETTINGS'; payload: PageSiteSettings }
  | { type: 'SET_COMPONENTS'; payload: WebsiteComponent[] }
  | { type: 'DELETE_PROJECT'; payload: { projectId: string } }
  | {
      type: 'UPDATE_DYNAMIC_ELEMENT';
      payload: { elementId: string; content: string }[];
    }
  | {
      type: 'SET_DYNAMIC_ELEMENT_VALUE';
      payload: { queryKey: string; value: string };
    }
  | {
      type: 'APPLY_PAGE_DYNAMIC_VALUES';
      payload: { queryKey: string; queryValue: string };
    }
  | {
      type: 'SELECT_DYNAMIC_VALUE';
      payload: { queryKey: string; value: string } | null;
    }
  | {
      type: 'UPDATE_DYNAMIC_VALUE_ELEMENT';
      payload: {
        dynamicContentId: string;
        elementType:
          | 'content'
          | 'src'
          | 'href'
          | 'interactionType'
          | 'linkUrl'
          | 'triggerPopup'
          | 'targetSectionId';
        content: string;
      };
    }
  | { type: 'RECOMPILE_TEMPLATE_WITH_GLOBAL_CONFIG' }
  | {
      type: 'MOVE_ELEMENT';
      payload: {
        elementId: string;
        targetContainerId: string;
        targetIndex?: number;
        parentTabId?: string;
      };
    }
  | {
      type: 'SET_CAROUSEL_ACTIVE_INDEX';
      payload: { elementId: string; index: number };
    }
  | {
      type: 'REORDER_ELEMENTS';
      payload: { containerId: string; fromIndex: number; toIndex: number };
    }
  | {
      type: 'INSERT_ELEMENT_TREE';
      payload: {
        element: BuilderElement;
        containerId?: string;
        targetIndex?: number;
        insertInto?: 'template' | 'popup';
      };
    };

export interface BuilderContextType {
  state: BuilderState;
  dispatch: React.Dispatch<BuilderAction>;
  getSelectedElement: () => BuilderElement | null;
  publiishAccessToken: string | null;
  selectedPreviewElement: BuilderElement | null;
  setSelectedPreviewElement: (element: BuilderElement | null) => void;
  markElementAsSynced: (elementId: string, isCreate: boolean) => void;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  saveStatus: 'idle' | 'saving' | 'success' | 'error';
}

export interface TemplateExtraPage {
  id?: string;
  title: string;
  industryName: Industry;
  dynamicValues?: TemplateExtraPageDynamicValues[];
}

export interface TemplateExtraPageDynamicValues {
  queryKey: string;
  values: {
    queryParamValue: string;
    dynamicElements: {
      elementId: string;
      content: string;
      elementType?:
        | 'content'
        | 'src'
        | 'href'
        | 'interactionType'
        | 'linkUrl'
        | 'triggerPopup'
        | 'targetSectionId'; // Indicates what property to update
      visible?: boolean; // Controls element visibility
    }[];
  }[];
}

export enum CUSTOM_CODE_ELEMENTS {
  START_OF_HEAD_TAG = 'startOfHeadTag',
  END_OF_HEAD_TAG = 'endOfHeadTag',
  START_OF_BODY_TAG = 'startOfBodyTag',
  END_OF_BODY_TAG = 'endOfBodyTag',
}

export interface SiteSettings {
  title: string;
  language: string;
  description: string;
  favicon?: File;
  favicon32?: File | string;
  favicon48?: File | string;
  favicon180?: File | string;
  socialPreview?: File;
  appleTouchIcon?: File;
  googleAnalyticsMeasurementId?: string;
  customCode: {
    [CUSTOM_CODE_ELEMENTS.START_OF_HEAD_TAG]?: string;
    [CUSTOM_CODE_ELEMENTS.END_OF_HEAD_TAG]?: string;
    [CUSTOM_CODE_ELEMENTS.START_OF_BODY_TAG]?: string;
    [CUSTOM_CODE_ELEMENTS.END_OF_BODY_TAG]?: string;
  };
  page: string;
  showPageOnSearchEngine: boolean;
  baseDomain?: {
    url: string;
    version?: string;
  };
  customDomain?: {
    type: 'free' | 'personal';
    personalDomain?: string;
    freeSubDomain: string;
  };
  // Brand fields
  brandLogo?: File | string | ResponsiveValue<File | string>;
  brandColors?: { [key: string]: string };
  // Project Metadata
  projectMetadata: {
    projectName: string;
    projectCode: string;
    stakeholders: string;
    owners: string;
    timeline: {
      designPhase: string;
      developmentPhase: string;
      qaPhase: string;
      goLivePhase: string;
    };
    deploymentTarget: string;
    cmsType: 'wordpress' | 'webflow' | 'html' | 'react' | 'nextjs' | 'other';
    environmentUrls: {
      staging: string;
      development: string;
      production: string;
    };
  };

  // Copy & Legal
  copyLegal: {
    finalCopyDocs: string;
    ctaVerbiage: string;
    legalApproval: boolean;
    emailContact: string;
    phoneContact: string;
  };

  // Performance & Accessibility
  performanceAccessibility: {
    imageOptimizationRules: {
      maxFileSize: number; // in KB
      maxDimensions: {
        width: number;
        height: number;
      };
      formats: string[];
    };
    lazyLoadSettings: {
      enabled: boolean;
      threshold: number;
    };
    accessibilityTags: {
      altTextRequired: boolean;
      tabIndexEnabled: boolean;
      colorContrastCheck: boolean;
    };
    fontLoadingStrategy: 'preload' | 'swap' | 'fallback';
    pageSpeedTargets: {
      mobileScore: number;
      desktopScore: number;
    };
  };

  // SEO
  seo: {
    metaTitle: string;
    metaDescription: string;
    canonicalTags: boolean;
    openGraphTags: {
      title: string;
      description: string;
      image: string;
      type: string;
    };
    altTextForSEO: boolean;
  };

  // Analytics & Tracking
  analyticsTracking: {
    gtmContainerId: string;
    ga4Setup: {
      measurementId: string;
      enabled: boolean;
    };
    conversionEvents: {
      scrollPercentage: number;
      ctaClicks: boolean;
      purchases: boolean;
      formSubmissions: boolean;
    };
    pixelIntegrations: {
      meta: string;
      tiktok: string;
      linkedin: string;
      googleAds: string;
    };
    consentGdpr: {
      cookieBanner: boolean;
      trackingOptIn: boolean;
      privacyPolicyUrl: string;
    };
  };

  // Tech Stack
  techStack: {
    frontendFramework:
      | 'react'
      | 'html'
      | 'webflow'
      | 'nextjs'
      | 'vue'
      | 'angular'
      | 'other';
    backendDependencies: string[];
    versionControl: 'github' | 'gitlab' | 'bitbucket' | 'other';
    hostingDeployment: 'netlify' | 'vercel' | 'cloudflare' | 'aws' | 'other';
  };

  // QA / Success
  qaSuccess: {
    deviceTesting: {
      iphone: boolean;
      android: boolean;
      tablets: boolean;
      desktop: boolean;
      browsers: string[];
    };
    coreWebVitals: {
      lcpTarget: number; // in seconds
      fidTarget: number; // in milliseconds
      clsTarget: number;
    };
    formSubmissionTest: {
      contactForm: boolean;
      upsellForm: boolean;
      newsletterForm: boolean;
    };
    fallbackChecks: {
      jsFailureHandling: boolean;
      missingParamHandling: boolean;
    };
    accessibilityScore: number; // target score
  };

  // Security & Privacy
  securityPrivacy: {
    sslCertificate: boolean;
    robotsTxtRules: string;
    noindexMetaTags: boolean;
    formSpamPrevention: {
      recaptcha: boolean;
      honeypot: boolean;
      cloudflare: boolean;
    };
  };

  // Add devicesQAMatrix for QA matrix table
  devicesQAMatrix?: {
    devices: { [device: string]: boolean };
    browsers: { [browser: string]: boolean };
    tests: { [testCategory: string]: boolean };
  };

  fallbackUrl?: string;
}

// Page-specific settings type
export interface PageSiteSettings {
  // Common properties for all pages
  title: string;
  language: string;
  description: string;
  showPageOnSearchEngine: boolean; // maps to API: indexing ('index' | 'noindex')
  pageType?: string; // maps to API: page_type (varchar 255)
  status?: 0 | 1; // maps to API: status (0 = inactive, 1 = active)
  customCode: {
    [CUSTOM_CODE_ELEMENTS.START_OF_HEAD_TAG]?: string;
    [CUSTOM_CODE_ELEMENTS.END_OF_HEAD_TAG]?: string;
    [CUSTOM_CODE_ELEMENTS.START_OF_BODY_TAG]?: string;
    [CUSTOM_CODE_ELEMENTS.END_OF_BODY_TAG]?: string;
  };

  // Dynamic Values Configuration - NEW
  dynamicValues?: {
    queryKey: string;
    values: {
      queryParamValue: string;
      dynamicElements: {
        elementId: string;
        content: string;
        elementType?:
          | 'content'
          | 'src'
          | 'href'
          | 'interactionType'
          | 'linkUrl'
          | 'triggerPopup'
          | 'targetSectionId'; // Indicates what property to update
      }[];
    }[];
  }[];

  // VSL Page specific properties
  videoSource?: {
    type: 'mp4' | 'youtube' | 'wistia';
    file?: File | string; // For MP4 uploads
    embed?: string; // For YouTube/Wistia embeds
  };
  videoPlaybackTriggerSeconds?: number;
  playbackSeekEventTriggered?: boolean;
  headlineBulletCopy?: File | string; // For approved copy document uploads
  anchorButtons?: Array<{
    buttonElementId: string;
    targetElementId: string;
  }>;
  exitIntentPopup?: {
    triggerElementId: string;
    discountPercentage: number;
  };
  complianceBadges?: Array<File | string>; // For badge image uploads

  // Upsell Page specific properties
  figmaDesign?: string;
  copyDocument?: string;
  acceptUrlPattern?: string;
  declineUrlPattern?: string;
  seoBlock?: string;
  assets?: string;
  stickyCTAMobile?: string;
  trackingEvents?: string;

  // Downsell Page specific properties
  trackingEventsDownsell?: string;
  figmaDesignDownsell?: string;
  reducedOfferCopy?: string;
  acceptRedirect?: string;
  declineRedirect?: string;
  seoBlockDownsell?: string;
  trackingEventsDownsell2?: string;

  // Thank You Page specific properties
  orderConfirmationBlock?: string;
  digistoreUpsellScript?: string;
  crossSellBanner?: string;
  customerSupportInfo?: string;
  seoBlockThankYou?: string;
  analyticsEvent?: string;

  // Privacy Policy Page specific properties
  legalCopy?: string;
  lastUpdatedDate?: string;
  contactEmail?: string;
  indexing?: boolean;

  // Disclaimer Page specific properties
  disclaimerCopy?: string;
  emailLink?: string;
  scientificReferenceLinks?: string;

  // Terms & Conditions Page specific properties
  tcCopy?: string;
  referenceLinks?: string;
  termsEmailLink?: string;
  termsIndexing?: boolean;

  // Returns & Refunds Page specific properties
  policyCopy?: string;
  supportEmailPhone?: string;
  returnAddress?: string;
  policyIndexing?: boolean;

  // Contact Us Page specific properties
  physicalAddress?: string;
  supportEmail?: string;
  phoneNumber?: string;
  contactFormEndpoint?: string;

  // Dynamic Element Visibility Controls
  // Buyer query parameter visibility controls
  buyerFirstTimeHeadlineVisible?: boolean;
  buyerFirstTimeDescriptionVisible?: boolean;
  buyerFirstTimeCtaVisible?: boolean;
  buyerReturningHeadlineVisible?: boolean;
  buyerReturningDescriptionVisible?: boolean;
  buyerReturningCtaVisible?: boolean;
  buyerVipHeadlineVisible?: boolean;
  buyerVipDescriptionVisible?: boolean;
  buyerVipCtaVisible?: boolean;
}

// VSL Page specific configuration
export interface VSLPageConfig {
  type: 'vsl';
  videoSource?: {
    type: 'mp4' | 'youtube' | 'wistia';
    file?: File | string; // For MP4 uploads
    embed?: string; // For YouTube/Wistia embeds
  };
  headlineBulletCopy?: File | string; // For approved copy document uploads
  anchorButtons?: Array<{
    buttonElementId: string;
    targetElementId: string;
  }>;
  exitIntentPopup?: {
    triggerElementId: string;
    discountPercentage: number;
  };
  complianceBadges?: Array<File | string>; // For badge image uploads
}

// Upsell Page specific configuration
export interface UpsellPageConfig {
  type: 'upsell';
  figmaDesign?: string;
  copyDocument?: string;
  acceptUrlPattern?: string;
  declineUrlPattern?: string;
  seoBlock?: string;
  assets?: string;
  stickyCTAMobile?: string;
  trackingEvents?: string;
}

// Downsell Page specific configuration
export interface DownsellPageConfig {
  type: 'downsell';
  trackingEventsDownsell?: string;
  figmaDesignDownsell?: string;
  reducedOfferCopy?: string;
  acceptRedirect?: string;
  declineRedirect?: string;
  seoBlockDownsell?: string;
  trackingEventsDownsell2?: string;
}

// Thank You Page specific configuration
export interface ThankYouPageConfig {
  type: 'thankyou';
  orderConfirmationBlock?: string;
  digistoreUpsellScript?: string;
  crossSellBanner?: string;
  customerSupportInfo?: string;
  seoBlockThankYou?: string;
  analyticsEvent?: string;
}

// Context Menu Types
export interface ContextMenuItem {
  label: string;
  action: (element: BuilderElement) => void;
  icon?: React.ReactNode;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
}

export type ContextMenuAction = 'copy' | 'paste';

export interface ContextMenuConfig {
  defaultItems: ContextMenuItem[];
  elementTypeSpecificItems?: {
    [K in ElementType]?: ContextMenuItem[];
  };
}
